
import { styled } from '@mui/material/styles';
import { Box, Typography, Table, TableHead, TableBody, TableCell, TextField, Tabs, Tab } from '@mui/material';
import { primaryColor } from '../../utils/theme';

export const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.palette.custom.borderRadius,
}));

export const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(3),
}));

export const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.8rem',
  fontWeight: 600,
  color: theme.palette.custom.sidebarText,
  whiteSpace: 'nowrap', // Prevent wrapping
}));

export const FilterBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.5),
  // Removed width: 100% to allow flex container to handle spacing naturally
}));

export const SearchInput = styled(TextField)(({ theme }) => ({
  width: 350, // Fixed width to accommodate placeholder
  maxWidth: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.white.main,
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&.Mui-focused fieldset': {
      borderColor: primaryColor,
      borderWidth: '1px',
    },
    '& .MuiInputAdornment-root': {
      color: theme.palette.grey[500],
    },
  },
}));

export const StatusTabsContainer = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  marginBottom: theme.spacing(3),
}));

export const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    backgroundColor: primaryColor,
  },
});

export const StatusTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  color: theme.palette.grey[600],
  padding: theme.spacing(1, 2),
  minHeight: '48px',
  '&.Mui-selected': {
    color: primaryColor,
  },
}));

export const StyledTableContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[200]}`,
  overflowX: 'auto',
  boxShadow: 'none',
}));

export const StyledTable = styled(Table)({
  minWidth: 800,
  borderCollapse: 'separate',
  borderSpacing: 0,
});

export const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light, // Changed to primary.light to match WorkerList

  '& th': {
    backgroundColor: 'inherit',
    color: theme.palette.custom.sidebarText, // Changed to sidebarText as per WorkerList, or white if manual override
    fontWeight: 600,
    fontSize: '0.85rem',
    borderBottom: 'none',
    padding: theme.spacing(1.5, 2),
  },
}));

// Override for the specific green header look if needed, but sticking to WorkerList default:
// WorkerList sets color: theme.palette.custom.sidebarText.
// But the screenshot shows White text on Green bg.
// If theme.palette.primary.light is the light grey, then `WorkerList` has a grey header.
// Looking at WorkerList/styles.ts:
// export const StyledTableHead = styled(TableHead)(({ theme }) => ({
//   backgroundColor: theme.palette.primary.light, 
//   '& th': { color: theme.palette.custom.sidebarText ... }
// }));
// AND `theme.palette.primary.light` is `secondaryColor` which is `originalTheme.colors.secondary` (#E1EBE3 - very light green/grey).
// BUT the screenshot provided by user (Image 1) shows a DARK GREEN header with WHITE text.
// If the user said "use our default table styles", they might be referring to a DIFFERENT table than WorkerList, OR they want this specific design.
// "create new page OrgReportAnalysis with this design" -> Refers to the IMAGE.
// "please use our default table and pagination styles" -> This is contradictory if the default style is light grey header.
// However, in the screenshot, the header IS Dark Green.
// I will stick to the DESIGN in the screenshot (Dark Green Header), but use the Pagination style from default.
// So I will revert StyledTableHead to use primary.main (Dark Green) and White text.

export const CustomStyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main, // Dark Green

  '& th': {
    backgroundColor: 'inherit',
    color: theme.palette.white.main, // White Text
    fontWeight: 600,
    fontSize: '0.85rem',
    borderBottom: 'none',
    padding: theme.spacing(1.5, 2),
  },
}));

export const StyledTableBody = styled(TableBody)(({ theme }) => ({
  '& tr:nth-of-type(odd)': {
    backgroundColor: theme.palette.white.main,
  },
  '& tr:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover, // Light stripe
  },
  '& tr:last-child td': {
    borderBottom: 0,
  },
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.85rem',
  color: theme.palette.custom.sidebarText,
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  padding: theme.spacing(1.5, 2),
}));


export const PaginationFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: theme.spacing(2),
  color: theme.palette.grey[600],
  fontSize: '0.85rem',
  paddingBottom: 0,
}));
